import { Box, dark } from '@pancakeswap/uikit'
import { ComponentProps } from 'react'
import styled, { css, keyframes, ThemeProvider } from 'styled-components'
import { Card, Chip, Countdown, Display, GhostCta, MONO, Num, PhasePill, Pill, PrimaryCta, fmtDate, useNow } from 'views/TradingCompetitionV3/ui'

/** The token pages extend the competition pages' look (night surfaces, hairlines, Gabarito / Figtree / Martian Mono); the moon is the new light source. */
export { Card, Chip, Countdown, Display, GhostCta, MONO, Num, PhasePill, Pill, PrimaryCta, fmtDate, useNow }
export { DISPLAY, Icon3d, Addr, surface } from 'views/TradingCompetitionV3/ui'

export const MOON = '#dfe9ff'
export const MOON_GLOW = 'rgba(159, 182, 255, 0.35)'

const PageBox = styled(Box)`
  position: relative;
  isolation: isolate;
  /* the island's own ground and text: inherited text (a price, a count) and the gaps between cards are night-coloured too, whatever the site theme is */
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.background};
  /* paint the ground to the screen's edges (box-shadow does not add scroll width); the page's own max-width still sets the content column */
  box-shadow: 0 0 0 100vmax ${({ theme }) => theme.colors.background};
  clip-path: inset(0 -100vmax);
  /* the night sky continues behind the page: a faint violet haze from the top */
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    left: 0;
    right: 0;
    top: 0;
    height: 560px;
    background: radial-gradient(70% 100% at 68% 0%, rgba(33, 0, 255, 0.16) 0%, rgba(126, 0, 254, 0.05) 45%, transparent 75%);
    pointer-events: none;
  }
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 16px 120px;
  overflow-x: clip;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 20px 24px 120px;
  }
`

/**
 * The token pages are a night scene whatever the site theme is: every surface, the moon and the text colours are designed on the dark palette.
 * The palette is a set of CSS variables that the site scopes to `[data-theme="dark"]` / `[data-theme="light"]`, so the subtree is switched by that
 * attribute (a styled-components ThemeProvider alone cannot change them: the variable names are the same in both themes). The provider stays for `isDark`.
 */
export const Page = ({ children, ...props }: ComponentProps<typeof PageBox>) => (
  <ThemeProvider theme={dark}>
    <PageBox {...props} data-theme="dark">
      {children}
    </PageBox>
  </ThemeProvider>
)

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`
export const Skel = styled.div<{ $h?: number; $w?: string; $r?: number }>`
  height: ${({ $h }) => $h ?? 12}px;
  width: ${({ $w }) => $w ?? '100%'};
  border-radius: ${({ $r }) => $r ?? 8}px;
  background: linear-gradient(90deg, rgba(180, 200, 255, 0.06) 25%, rgba(180, 200, 255, 0.16) 50%, rgba(180, 200, 255, 0.06) 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/** Equal-width segmented control (tabs, Buy/Sell, timeframes): one line, reserved space, 44px targets. */
export const Segmented = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols ?? 2}, minmax(0, 1fr));
  gap: 4px;
  padding: 4px;
  border-radius: 16px;
  background: rgba(5, 7, 19, 0.6);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
`
/** Tabs that stay equal-width and on one line: a grid of `$mobile` columns on a phone, `$cols` from tablet width up. */
export const TabBar = styled(Segmented)<{ $mobile: number }>`
  grid-template-columns: repeat(${({ $mobile }) => $mobile}, minmax(0, 1fr));
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(${({ $cols }) => $cols ?? 2}, minmax(0, 1fr));
  }
`
export const SegBtn = styled.button<{ $on?: boolean; $tone?: 'up' | 'down' }>`
  min-height: 44px;
  min-width: 0;
  padding: 0 8px;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme, $on, $tone }) => ($on ? ($tone ? '#050713' : theme.colors.text) : theme.colors.textSubtle)};
  background: ${({ theme, $on, $tone }) => ($on ? ($tone === 'up' ? theme.colors.success : $tone === 'down' ? theme.colors.failure : 'rgba(127, 212, 232, 0.16)') : 'transparent')};
  box-shadow: ${({ $on, $tone }) => ($on ? ($tone ? 'inset 0 1px 0 rgba(255,255,255,0.2)' : 'inset 0 0 0 1px rgba(127, 212, 232, 0.55), inset 0 1px 0 rgba(255,255,255,0.1)') : 'none')};
  &:hover {
    color: ${({ theme, $on, $tone }) => ($on && $tone ? '#050713' : theme.colors.text)};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`

export const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: 14px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSubtle};
  background: rgba(5, 7, 19, 0.5);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  text-decoration: none;
  transition: color 0.15s ease, border-color 0.15s ease;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

/** The secondary call to action: an ice-tinted fill (not the violet gradient, which belongs to the page's one primary action), 48 px, centred, full width on a phone. */
export const SoftCta = styled.a<{ $block?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 800;
  font-size: 15px;
  text-decoration: none;
  text-align: center;
  color: #d7f3fb;
  background: rgba(127, 212, 232, 0.14);
  border: 1px solid rgba(127, 212, 232, 0.5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: rgba(127, 212, 232, 0.22);
    border-color: rgba(127, 212, 232, 0.8);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    width: auto;
  }
  /* && raises the specificity above the media query's width:auto (stylis emits media rules after the base rule, whatever the source order) */
  ${({ $block }) => ($block ? '&& { width: 100%; }' : '')}
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: 'Gabarito', 'Gabarito Fallback', system-ui, sans-serif;
  font-weight: 800;
  font-size: 20px;
  line-height: 26px;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text};
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 24px;
    line-height: 30px;
  }
`

/** The moon-lit panel: the competition pages' surface with a cooler top edge. */
export const moonSurface = css`
  background: linear-gradient(180deg, rgba(159, 182, 255, 0.06) 0%, transparent 38%), ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 20px;
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.35);
  min-width: 0;
`
export const Panel = styled(Box)`
  ${moonSurface}
  padding: 16px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 20px;
  }
`

export const Tone = {
  up: (t: any) => t.colors.success,
  down: (t: any) => t.colors.failure,
  flat: (t: any) => t.colors.textSubtle,
}
